import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from "react";
import { useNavigate } from "@tanstack/react-router";
import { FileEdit, Link as LinkIcon, MessageCircle, Search, type LucideIcon } from "lucide-react";
import { flatNavItems } from "@/lib/navigation";
import { fuzzyFilter } from "@/lib/fuzzy";
import { cn } from "@/lib/utils";

const OPEN_EVENT = "lucralink:open-command-palette";

interface Command {
  readonly id: string;
  readonly label: string;
  readonly hint: string;
  readonly to: string;
  readonly icon: LucideIcon;
  readonly keywords: readonly string[];
}

const QUICK_ACTIONS: readonly Command[] = [
  { id: "action:link", label: "Gerar link de afiliado", hint: "Ação rápida", to: "/app/links", icon: LinkIcon, keywords: ["converter", "afiliado"] },
  { id: "action:whatsapp", label: "Conectar WhatsApp", hint: "Ação rápida", to: "/app/settings/integracao", icon: MessageCircle, keywords: ["zap", "numero", "qr"] },
  { id: "action:post", label: "Nova publicação", hint: "Ação rápida", to: "/app/criar-posts", icon: FileEdit, keywords: ["post", "disparo", "agendar"] },
];

function buildCommands(): Command[] {
  const pages = flatNavItems().map((item) => ({
    id: `page:${item.to}`,
    label: item.label,
    hint: item.section ?? "Página",
    to: item.to,
    icon: item.icon,
    keywords: [item.section ?? ""],
  }));
  return [...QUICK_ACTIONS, ...pages];
}

/** Abre a palette de qualquer lugar (botao da sidebar). */
export function openCommandPalette(): void {
  window.dispatchEvent(new CustomEvent(OPEN_EVENT));
}

function isMac(): boolean {
  return typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);
}

export function CommandPaletteTrigger(): JSX.Element {
  return (
    <button
      type="button"
      onClick={openCommandPalette}
      className="flex h-9 w-full items-center gap-2 rounded-lg border border-border bg-background/40 px-3 text-sm text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <Search size={14} aria-hidden="true" />
      <span className="flex-1 text-left">Buscar</span>
      <kbd className="rounded border border-border px-1.5 font-mono text-[10px]">{isMac() ? "⌘K" : "Ctrl K"}</kbd>
    </button>
  );
}

function usePaletteShortcut(setOpen: (fn: (open: boolean) => boolean) => void): void {
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setOpen((open) => !open);
      }
    };
    const onOpen = (): void => setOpen(() => true);
    window.addEventListener("keydown", onKey);
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener(OPEN_EVENT, onOpen);
    };
  }, [setOpen]);
}

/** Command palette (Cmd/Ctrl+K): navegacao para todas as telas e acoes rapidas,
 *  busca fuzzy, setas para escolher, Enter para abrir, Esc para fechar. */
export function CommandPalette(): JSX.Element | null {
  const [open, setOpen] = useState(false);
  usePaletteShortcut(setOpen);
  if (!open) return null;
  return <PaletteDialog onClose={() => setOpen(false)} />;
}

function PaletteDialog({ onClose }: { onClose: () => void }): JSX.Element {
  const navigate = useNavigate();
  const listId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const commands = useMemo(buildCommands, []);
  const results = useMemo(() => fuzzyFilter(commands, query, (c) => [c.label, ...c.keywords]), [commands, query]);
  const current = results[Math.min(active, results.length - 1)];

  useEffect(() => inputRef.current?.focus(), []);

  const run = (command: Command | undefined): void => {
    if (!command) return;
    onClose();
    void navigate({ to: command.to });
  };

  const onKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>): void => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const step = event.key === "ArrowDown" ? 1 : -1;
      setActive((i) => (results.length === 0 ? 0 : (Math.min(i, results.length - 1) + step + results.length) % results.length));
    } else if (event.key === "Enter") {
      event.preventDefault();
      run(current);
    } else if (event.key === "Escape") {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center px-4 pt-[12vh]" role="dialog" aria-modal="true" aria-label="Busca rápida">
      <button type="button" aria-label="Fechar busca" className="absolute inset-0 bg-background/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg overflow-hidden rounded-xl border border-border bg-card shadow-2xl">
        <div className="flex items-center gap-2 border-b border-border px-4">
          <Search size={16} className="text-muted-foreground" aria-hidden="true" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActive(0);
            }}
            onKeyDown={onKeyDown}
            role="combobox"
            aria-expanded="true"
            aria-controls={listId}
            aria-activedescendant={current ? `${listId}-${current.id}` : undefined}
            aria-autocomplete="list"
            placeholder="Buscar telas e ações..."
            className="h-12 w-full bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
          />
        </div>
        <ul id={listId} role="listbox" aria-label="Resultados" className="max-h-[50vh] overflow-y-auto p-2">
          {results.length === 0 && <li className="px-3 py-6 text-center text-sm text-muted-foreground">Nada encontrado.</li>}
          {results.map((command) => {
            const Icon = command.icon;
            const selected = command === current;
            return (
              <li
                key={command.id}
                id={`${listId}-${command.id}`}
                role="option"
                aria-selected={selected}
                onMouseEnter={() => setActive(results.indexOf(command))}
                onClick={() => run(command)}
                className={cn("flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-sm", selected ? "bg-primary/10 text-foreground" : "text-muted-foreground")}
              >
                <Icon size={15} aria-hidden="true" className={selected ? "text-primary" : undefined} />
                <span className="flex-1 truncate">{command.label}</span>
                <span className="text-[11px] text-muted-foreground">{command.hint}</span>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
